import { useState } from 'react'
import { useSnapshot } from 'valtio'
import { openOptionsMenu } from '../../globalState'
import { AppOptions, options } from '../../optionsStorage'
import { BoltIcon, GearIcon, LayersIcon, StackIcon } from './icons'
import { NoticeCard } from './SettingsUi'
import styles from './SettingsUi.module.css'

type PresetId = 'min' | 'medium' | 'max'

/**
 * Presets from the "preset render" table of the mockup. Two rows have no exact
 * option behind them: the 10 FPS background limit lands on the nearest step
 * (5 FPS), and "Auto / Low Power" resolves to low-power.
 */
const PRESETS: Record<PresetId, Partial<AppOptions> & { effects: boolean }> = {
  max: {
    frameLimit: false, // unlimited — the display syncs it
    gpuPreference: 'high-performance',
    backgroundRendering: '20fps',
    dayCycleAndLighting: true,
    smoothLighting: true,
    lowMemoryMode: false,
    starfieldRendering: true,
    renderEntities: true,
    effects: true,
  },
  medium: {
    frameLimit: 60,
    gpuPreference: 'default',
    backgroundRendering: '20fps',
    dayCycleAndLighting: true,
    smoothLighting: true,
    lowMemoryMode: false,
    starfieldRendering: true,
    renderEntities: true,
    effects: true,
  },
  min: {
    frameLimit: 30,
    gpuPreference: 'low-power',
    backgroundRendering: '5fps',
    dayCycleAndLighting: false,
    smoothLighting: false,
    lowMemoryMode: true,
    starfieldRendering: false,
    renderEntities: false,
    effects: false,
  },
}

const PRESET_IDS: PresetId[] = ['min', 'medium', 'max']

/** Highlighted when the current options match no preset. */
const DEFAULT_PRESET: PresetId = 'medium'

const PRESET_BUTTONS = [
  { id: 'min', label: 'Мин.', icon: <LayersIcon size={16} /> },
  { id: 'medium', label: 'Средн.', icon: <StackIcon size={16} /> },
  { id: 'max', label: 'Макс.', icon: <BoltIcon size={16} /> },
] as const

// valtio hands out a deeply readonly snapshot, hence the loose shape here.
type OptionsSnapshot = Record<string, any> & { disabledUiParts: readonly string[] }

const matches = (snapshot: OptionsSnapshot, preset: typeof PRESETS[PresetId]) => {
  const { effects, ...rest } = preset
  const effectsShown = !snapshot.disabledUiParts.includes('effects')
  return effectsShown === effects
    && Object.entries(rest).every(([key, value]) => snapshot[key] === value)
}

const apply = (preset: typeof PRESETS[PresetId]) => {
  const { effects, ...rest } = preset
  Object.assign(options, rest)
  const hidden = options.disabledUiParts.includes('effects')
  if (effects === hidden) {
    options.disabledUiParts = effects
      ? options.disabledUiParts.filter(part => part !== 'effects')
      : [...options.disabledUiParts, 'effects']
  }
}

/**
 * Preset picker for the root screen. The active preset is derived from the
 * options themselves; a custom mix from the Рендер screen falls back to Средн.
 * so the row is never left without a selection.
 */
export const RenderQualityField = () => {
  const snapshot = useSnapshot(options)
  const [noticeDismissed, setNoticeDismissed] = useState(false)

  const active = PRESET_IDS.find(id => matches(snapshot, PRESETS[id])) ?? DEFAULT_PRESET

  return <div className={styles.field}>
    <div className={styles.fieldTitle}>Качество рендера</div>
    <div className={styles.segmentedRow}>
      <button
        type='button'
        className={styles.segment}
        title='Настройки рендера'
        onClick={() => openOptionsMenu('render')}
      >
        <GearIcon size={16} />
      </button>
      <span className={styles.rowDivider} />
      {PRESET_BUTTONS.map(({ id, label, icon }) => (
        <button
          key={id}
          type='button'
          className={`${styles.segment} ${styles.segmentWide} ${id === active ? styles.segmentActive : ''}`}
          onClick={() => {
            apply(PRESETS[id])
            setNoticeDismissed(false)
          }}
        >
          {icon}
          {label}
        </button>
      ))}
    </div>
    {active === 'min' && !noticeDismissed && (
      <NoticeCard title='Пониженное качество графики' onDismiss={() => setNoticeDismissed(true)}>
        Отключает отображение мобов и сущностей, уменьшает дистанцию прорисовки чанков для повышения производительности
      </NoticeCard>
    )}
  </div>
}
